
import React, { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import LeadGenerator from '../ai-assistant/LeadGenerator';
import { useAIAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import { Button } from '@/components/ui/button';

interface LeadFieldProps {
  lead: string;
  setLead: (lead: string) => void;
  content?: string;
}

const LeadField: React.FC<LeadFieldProps> = ({ lead, setLead, content = '' }) => {
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false);
  const { isProcessing, activeFeature } = useAIAssistant();

  const handleLeadSelect = (selectedLead: string) => {
    setLead(selectedLead);
    setIsLeadDialogOpen(false);
  };

  return (
    <Card className="border-border/40 shadow-sm overflow-hidden transition-all duration-200 hover:shadow hover:border-border/60">
      <CardContent className="p-5 space-y-3">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="lead-paragraph" className="text-lg font-medium">
              Lead Paragraph
            </Label>
            <Dialog open={isLeadDialogOpen} onOpenChange={setIsLeadDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost"
                  size="sm"
                  className="text-yellow-500 hover:text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
                  disabled={isProcessing || !content}
                >
                  {activeFeature === 'generateLead' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <LeadGenerator
                  content={content}
                  currentLead={lead}
                  onSelectLead={handleLeadSelect}
                  onClose={() => setIsLeadDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        <Textarea
          id="lead-paragraph"
          placeholder="Write a compelling lead paragraph that summarizes your article..."
          value={lead}
          onChange={(e) => setLead(e.target.value)}
          rows={4}
          className="w-full resize-none text-base leading-relaxed font-serif focus:border-primary/40 focus:ring-1 focus:ring-primary/30"
        />
        
        <p className="text-xs text-muted-foreground italic">
          The lead paragraph is the opening of your article and should capture the reader's attention.
          It typically summarizes the main points of the article.
        </p>
      </CardContent>
    </Card>
  );
};

export default LeadField;
